/**
 * Unsaved-changes detection for the Add/Edit contact form.
 *
 * Kept out of AddContactModal.tsx for two reasons. The form owns 20 inputs, so
 * a close guard needs one honest predicate rather than 20 inline comparisons
 * spread across every close path. And a pure, dependency-free comparison can
 * be unit-tested directly, without mounting the modal.
 */

/** Every field the form writes into `formData`, in the order it renders. */
export const CONTACT_FORM_FIELDS = [
  'email',
  'first_name',
  'last_name',
  'job_title',
  'department',
  'seniority_level',
  'company_name',
  'company_website',
  'company_industry',
  'company_size',
  'phone',
  'linkedin_url',
  'twitter_url',
  'city',
  'state',
  'country',
] as const

export type ContactFormField = (typeof CONTACT_FORM_FIELDS)[number]

/** The live form state — an <input> always hands back a string. */
export type ContactFormState = { [K in ContactFormField]: string }

/**
 * Values as the comparison receives them. The form itself is all strings, but
 * the edit path seeds its baseline from a `Contact` row whose optional columns
 * arrive as `null`/`undefined`, so both are accepted here.
 */
export type ContactFormValues = { [K in ContactFormField]: string | null | undefined }

/**
 * An unset column (`null`/`undefined`) and an untouched input (`''`) mean the
 * same thing to the user, and surrounding whitespace is not content — typing a
 * space into an empty field must not arm the discard prompt.
 */
function normalize(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * Tags are an unordered selection: picking A then B is the same contact as
 * picking B then A, so they compare as a set rather than as a list. Blank
 * entries collapse away for the same reason `normalize` trims fields.
 */
function tagSet(tags: string[]): Set<string> {
  return new Set(tags.map((tag) => tag.trim()).filter(Boolean))
}

/**
 * True when the user has changed anything since the modal seeded itself.
 * `baseline` is what was loaded on open (an existing contact, or an empty
 * form), never a constant — comparing an edit against empty would report
 * every open as dirty.
 */
export function isContactFormDirty(
  current: ContactFormValues,
  baseline: ContactFormValues,
  currentTags: string[],
  baselineTags: string[],
): boolean {
  for (const field of CONTACT_FORM_FIELDS) {
    if (normalize(current[field]) !== normalize(baseline[field])) return true
  }

  const currentSet = tagSet(currentTags)
  const baselineSet = tagSet(baselineTags)
  if (currentSet.size !== baselineSet.size) return true
  for (const tag of currentSet) {
    if (!baselineSet.has(tag)) return true
  }

  return false
}
